import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import { useState } from 'react'
import {
  Card,
  CopyButton,
  Empty,
  ErrorNote,
  Eyebrow,
  Field,
  Kpi,
  PageHead,
  Spinner,
  Status,
  Table,
} from '../../components/ui'
import { api } from '../../lib/api'
import { fmtDate, fmtMoney, fmtNum, rewardText } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useMe } from '../../lib/session'

interface Overview {
  me: { name: string; email: string }
  programs: {
    id: string
    name: string
    slug: string
    website: string
    currency: string
    status: string
    clicks: number
    sales: number
    pending: string
    available: string
    paid: string
    next_release: string | null
  }[]
  payoutMethod: { provider: string; summary: string; verifiedAt: string | null; changedAt: string } | null
}

export function PartnerOverviewPage() {
  const { t, locale } = useT()
  const me = useMe()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['p-overview'],
    queryFn: () => api<Overview>('/api/partner/overview'),
    retry: false,
    enabled: !!me.data?.partner,
  })
  const create = useMutation({
    mutationFn: () => api('/api/partner/profile', { body: {} }),
    onSuccess: () => qc.invalidateQueries(),
  })
  if (!me.data?.partner) {
    return (
      <Empty
        title={t('partnerDashboard')}
        text={t('noProgramsText')}
        action={
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => create.mutate()}
            disabled={create.isPending}
          >
            {t('becomePartnerProfile')}
          </button>
        }
      />
    )
  }
  if (!q.data) return <Spinner />
  const progs = q.data.programs
  const cur = progs[0]?.currency ?? 'USD'
  const sum = (k: 'pending' | 'available' | 'paid') =>
    progs.filter((p) => p.currency === cur).reduce((s, p) => s + Number(p[k]), 0)
  const next =
    progs
      .map((p) => p.next_release)
      .filter(Boolean)
      .sort()[0] ?? null
  return (
    <div>
      <PageHead eyebrow={q.data.me.email} title={t('overview')} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label={t('pendingHold')} value={fmtMoney(sum('pending'), cur, locale)} />
        <Kpi
          label={t('available')}
          value={fmtMoney(sum('available'), cur, locale)}
          tone="ok"
          sub={q.data.payoutMethod ? q.data.payoutMethod.summary : t('setMethod')}
        />
        <Kpi label={t('paid')} value={fmtMoney(sum('paid'), cur, locale)} />
        <Kpi
          label={t('nextPayout')}
          value={<span className="text-xl">{next ? fmtDate(next, locale) : '—'}</span>}
        />
      </div>
      {!q.data.payoutMethod && sum('available') > 0 && (
        <Card className="glow mt-3 flex flex-wrap items-center justify-between gap-4">
          <span>{t('setMethod')}</span>
          <Link to="/partner/payouts" className="btn btn-primary btn-sm">
            {t('setMethod')} →
          </Link>
        </Card>
      )}
      <div className="mb-4 mt-10">
        <Eyebrow>{t('programs')}</Eyebrow>
      </div>
      {!progs.length ? (
        <Empty title={t('noPrograms')} text={t('noProgramsText')} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {progs.map((p) => (
            <Link
              key={p.id}
              to="/partner/programs/$id"
              params={{ id: p.id }}
              className="card card-pad block text-ink no-underline transition hover:border-white/30"
            >
              <div className="mb-4 flex items-center justify-between">
                <span className="text-xl tracking-tight">{p.name}</span>
                <Status value={p.status} kind="enrollment" />
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="eyebrow mb-1">{t('clicks')}</div>
                  <div className="mono">{fmtNum(p.clicks, locale)}</div>
                </div>
                <div>
                  <div className="eyebrow mb-1">{t('sales')}</div>
                  <div className="mono">{fmtNum(p.sales, locale)}</div>
                </div>
                <div>
                  <div className="eyebrow mb-1">{t('earnings')}</div>
                  <div className="mono">
                    {fmtMoney(Number(p.pending) + Number(p.available) + Number(p.paid), p.currency, locale)}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

interface ProgramDetail {
  enrollment: { status: string }
  group: { name: string; slug: string } | null
  program: { id: string; name: string; website: string; description: string | null; currency: string }
  terms: {
    rewards: { event: string; type: string; bps?: number; amountMinor?: number }[]
    holdDays: number
    minPayoutMinor: number
  }
  links: { id: string; code: string; destination: string; subId: string | null; clicks: number }[]
  coupons: { code: string }[]
  linksOrigin: string
}

export function PartnerProgramPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const { t, locale } = useT()
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['p-program', id],
    queryFn: () => api<ProgramDetail>(`/api/partner/programs/${id}`),
  })
  const [f, setF] = useState({ code: '', subId: '', destination: '' })
  const create = useMutation({
    mutationFn: () =>
      api('/api/partner/links', {
        body: {
          programId: id,
          ...(f.code ? { code: f.code.toLowerCase() } : {}),
          ...(f.subId ? { subId: f.subId } : {}),
          ...(f.destination ? { destination: f.destination } : {}),
        },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['p-program', id] })
      setF({ code: '', subId: '', destination: '' })
    },
  })
  const archive = useMutation({
    mutationFn: (linkId: string) => api(`/api/partner/links/${linkId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['p-program', id] }),
  })
  if (q.error) return <ErrorNote error={q.error} />
  if (!q.data) return <Spinner />
  const d = q.data
  const approved = d.enrollment.status === 'approved'
  return (
    <div>
      <PageHead
        eyebrow={d.program.website.replace(/^https?:\/\//, '')}
        title={d.program.name}
        actions={<Status value={d.enrollment.status} kind="enrollment" />}
      />
      <div className="mb-6 flex flex-wrap gap-2">
        {d.group && (
          <span className="badge">
            {t('yourGroup')}: {d.group.name}
          </span>
        )}
        {d.terms.rewards.map((r) => (
          <span key={r.event} className="badge badge-ok">
            {rewardText(r, d.program.currency, locale)} · {r.event}
          </span>
        ))}
        <span className="badge">
          {t('holdDays')}: {d.terms.holdDays} {t('days')}
        </span>
        <span className="badge">
          {t('payoutsFrom', { amount: fmtMoney(d.terms.minPayoutMinor, d.program.currency, locale) })}
        </span>
      </div>
      {!approved ? (
        <Empty title={t('pendingReview')} text={t('applyDoneText')} />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr_1.5fr]">
          <Card>
            <Eyebrow className="mb-6">{t('createLink')}</Eyebrow>
            <form
              className="grid gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                create.mutate()
              }}
            >
              <Field label={t('code')} hint={`${d.linksOrigin}/${f.code || 'your-code'}`}>
                <input
                  className="input mono"
                  pattern="[a-z0-9][a-z0-9\-]{1,30}[a-z0-9]"
                  value={f.code}
                  onChange={(e) => setF({ ...f, code: e.target.value.toLowerCase() })}
                  placeholder="jane-yt"
                />
              </Field>
              <Field label={t('subId')}>
                <input
                  className="input mono"
                  pattern="[A-Za-z0-9_.\-]{1,64}"
                  value={f.subId}
                  onChange={(e) => setF({ ...f, subId: e.target.value })}
                  placeholder="youtube-oct"
                />
              </Field>
              <Field label={t('destination')}>
                <input
                  className="input"
                  type="url"
                  value={f.destination}
                  onChange={(e) => setF({ ...f, destination: e.target.value })}
                  placeholder={d.program.website}
                />
              </Field>
              <button
                type="submit"
                className="btn btn-primary justify-self-start"
                disabled={create.isPending}
              >
                {t('createLink')}
              </button>
              <ErrorNote error={create.error} />
            </form>
            {!!d.coupons.length && (
              <div className="mt-8 border-t border-hairline pt-6">
                <Eyebrow className="mb-3">{t('coupon')}</Eyebrow>
                {d.coupons.map((c) => (
                  <div key={c.code} className="mono flex items-center justify-between text-lg">
                    {c.code} <CopyButton value={c.code} />
                  </div>
                ))}
              </div>
            )}
          </Card>
          <div>
            <Eyebrow className="mb-3">{t('yourLinks')}</Eyebrow>
            {!d.links.length ? (
              <Empty title={t('yourLinks')} text={t('createLink')} />
            ) : (
              <div className="grid gap-3">
                {d.links.map((l) => {
                  const url = `${d.linksOrigin}/${l.code}${l.subId ? `?sub=${encodeURIComponent(l.subId)}` : ''}`
                  return (
                    <div key={l.id} className="card card-pad">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <span className="mono break-all text-ink">{url}</span>
                        <div className="flex gap-2">
                          <CopyButton value={url} />
                          <button
                            type="button"
                            className="btn btn-sm btn-ghost"
                            onClick={() => archive.mutate(l.id)}
                          >
                            {t('remove')}
                          </button>
                        </div>
                      </div>
                      <div className="mono mt-3 flex gap-4 text-xs text-mute">
                        <span>
                          {fmtNum(l.clicks, locale)} {t('clicks').toLowerCase()}
                        </span>
                        <span className="truncate">→ {l.destination}</span>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export function PartnerEarningsPage() {
  const { t, locale } = useT()
  const q = useQuery({
    queryKey: ['p-commissions'],
    queryFn: () =>
      api<{
        commissions: {
          id: string
          status: string
          amount_minor: string
          sale_minor: string | null
          currency: string
          available_at: string
          created_at: string
          program_name: string
          hold_reason: string | null
        }[]
      }>('/api/partner/commissions'),
  })
  if (!q.data) return <Spinner />
  return (
    <div>
      <PageHead title={t('earnings')} />
      {!q.data.commissions.length ? (
        <Empty title={t('emptyCommissions')} text={t('noProgramsText')} />
      ) : (
        <Table head={[t('program'), t('amount'), t('sale'), t('status'), t('holdEnds'), t('created')]}>
          {q.data.commissions.map((c) => (
            <tr key={c.id}>
              <td className="text-ink">{c.program_name}</td>
              <td className="mono text-ink">{fmtMoney(c.amount_minor, c.currency, locale)}</td>
              <td className="mono">{c.sale_minor ? fmtMoney(c.sale_minor, c.currency, locale) : '—'}</td>
              <td>
                <Status value={c.status} />
              </td>
              <td className="mono text-xs">
                {c.status === 'pending' ? fmtDate(c.available_at, locale) : '—'}
              </td>
              <td className="mono text-xs">{fmtDate(c.created_at, locale)}</td>
            </tr>
          ))}
        </Table>
      )}
    </div>
  )
}

export function PartnerPayoutsPage() {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const ov = useQuery({ queryKey: ['p-overview'], queryFn: () => api<Overview>('/api/partner/overview') })
  const q = useQuery({
    queryKey: ['p-payouts'],
    queryFn: () =>
      api<{
        payouts: {
          id: string
          kind: 'payout' | 'verification'
          awaiting_confirmation: boolean | null
          status: string
          amount_minor: string
          adjustment_minor: string
          currency: string
          provider: string
          provider_ref: string | null
          created_at: string
          paid_at: string | null
          workspace_name: string | null
        }[]
      }>('/api/partner/payouts'),
  })
  const confirmTest = useMutation({
    mutationFn: (id: string) => api(`/api/partner/payouts/${id}/confirm-test`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries(),
  })
  const pendingTest = q.data?.payouts.find((p) => p.awaiting_confirmation)
  const [f, setF] = useState({
    provider: 'paypal',
    email: '',
    accountId: '',
    asset: 'USDT',
    network: 'TRC20',
    address: '',
  })
  const save = useMutation({
    mutationFn: () =>
      api('/api/partner/payout-method', {
        method: 'PUT',
        body:
          f.provider === 'paypal'
            ? { provider: 'paypal', email: f.email }
            : f.provider === 'stripe'
              ? { provider: 'stripe', accountId: f.accountId }
              : { provider: 'crypto_manual', asset: f.asset, network: f.network, address: f.address },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['p-overview'] }),
  })
  const m = ov.data?.payoutMethod
  const cooldown = m?.verifiedAt && Date.now() - new Date(m.changedAt).getTime() < 72 * 3600_000
  return (
    <div>
      <PageHead
        title={t('payouts')}
        actions={
          <a className="btn" href="/api/partner/payouts.csv">
            {t('statement')}
          </a>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Card>
          <Eyebrow className="mb-4">{t('method')}</Eyebrow>
          {m && (
            <div className="mb-6 rounded-lg border border-hairline p-4">
              <div className="text-ink">{m.summary}</div>
              <div
                className={`mono mt-2 text-xs ${m.verifiedAt ? (cooldown ? 'text-sunset' : 'text-ok') : 'text-sunset'}`}
              >
                {m.verifiedAt ? (cooldown ? t('methodCooldown') : t('methodVerified')) : t('methodPending')}
              </div>
            </div>
          )}
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              save.mutate()
            }}
          >
            <div className="flex flex-wrap gap-1">
              {[
                ['paypal', 'PayPal'],
                ['stripe', 'Stripe'],
                ['crypto_manual', 'USDT / USDC'],
              ].map(([v, l]) => (
                <button
                  key={v}
                  type="button"
                  className={`tab ${f.provider === v ? 'on' : ''}`}
                  onClick={() => setF({ ...f, provider: v! })}
                >
                  {l}
                </button>
              ))}
            </div>
            {f.provider === 'paypal' && (
              <Field label={t('paypalEmail')}>
                <input
                  className="input"
                  type="email"
                  required
                  value={f.email}
                  onChange={(e) => setF({ ...f, email: e.target.value })}
                />
              </Field>
            )}
            {f.provider === 'stripe' && (
              <Field label={t('stripeAccount')}>
                <input
                  className="input mono"
                  required
                  pattern="acct_[A-Za-z0-9]{8,}"
                  value={f.accountId}
                  onChange={(e) => setF({ ...f, accountId: e.target.value.trim() })}
                />
              </Field>
            )}
            {f.provider === 'crypto_manual' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <Field label={t('asset')}>
                    <select
                      className="select"
                      value={f.asset}
                      onChange={(e) => setF({ ...f, asset: e.target.value })}
                    >
                      <option>USDT</option>
                      <option>USDC</option>
                    </select>
                  </Field>
                  <Field label={t('network')}>
                    <select
                      className="select"
                      value={f.network}
                      onChange={(e) => setF({ ...f, network: e.target.value })}
                    >
                      {['TRC20', 'ERC20', 'POLYGON', 'BASE', 'SOLANA'].map((n) => (
                        <option key={n}>{n}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <Field label={t('address')}>
                  <input
                    className="input mono"
                    required
                    value={f.address}
                    onChange={(e) => setF({ ...f, address: e.target.value.trim() })}
                  />
                </Field>
              </>
            )}
            <button type="submit" className="btn btn-primary justify-self-start" disabled={save.isPending}>
              {t('save')}
            </button>
            {save.isSuccess && <div className="text-sm text-ok">{t('confirmEmail')}</div>}
            <ErrorNote error={save.error} />
          </form>
          <div className="mt-8 border-t border-hairline pt-6 text-sm text-mute">
            {t('mfaOptional')}{' '}
            <a className="text-ink" href="/mfa?next=/partner/payouts">
              {t('enable2fa')} →
            </a>
          </div>
        </Card>
        <div>
          {pendingTest && (
            <Card className="glow mb-4">
              <p className="mb-4 text-sm text-body">{t('testTransferHint')}</p>
              <div className="flex flex-wrap items-center gap-3">
                <span className="mono text-ink">
                  {fmtMoney(pendingTest.amount_minor, pendingTest.currency, locale)}
                </span>
                <span className="mono truncate text-xs text-mute">{pendingTest.provider_ref}</span>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => confirmTest.mutate(pendingTest.id)}
                  disabled={confirmTest.isPending}
                >
                  {t('confirmReceipt')}
                </button>
              </div>
              <ErrorNote error={confirmTest.error} />
            </Card>
          )}
          <Eyebrow className="mb-3">{t('history')}</Eyebrow>
          {!q.data?.payouts.length ? (
            <Empty title={t('history')} text={t('noPayable')} />
          ) : (
            <Table head={[t('amount'), t('provider'), t('status'), t('created')]}>
              {q.data.payouts.map((p) => (
                <tr key={p.id}>
                  <td className="mono text-ink">
                    {fmtMoney(p.amount_minor, p.currency, locale)}
                    <div className="text-xs text-mute">{p.workspace_name}</div>
                  </td>
                  <td className="text-xs">
                    {p.provider}
                    {p.kind === 'verification' && (
                      <div className="mt-1">
                        <span className="badge badge-info">{t('testTransfer')}</span>
                      </div>
                    )}
                  </td>
                  <td>
                    <Status value={p.status} />
                  </td>
                  <td className="mono text-xs">{fmtDate(p.paid_at ?? p.created_at, locale)}</td>
                </tr>
              ))}
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}

export function ConfirmPayoutPage() {
  const { t } = useT()
  const token = new URLSearchParams(location.search).get('token') ?? ''
  const m = useQuery({
    queryKey: ['confirm', token],
    queryFn: () => api('/api/partner/payout-method/confirm', { body: { token } }),
    retry: false,
  })
  return (
    <Card className="mx-auto mt-16 max-w-md text-center">
      {m.isLoading ? (
        <Spinner />
      ) : m.isSuccess ? (
        <p className="text-ok">{t('confirmPayoutDone')}</p>
      ) : (
        <p className="text-danger">{t('confirmPayoutFail')}</p>
      )}
      <Link to="/partner/payouts" className="btn mt-6">
        {t('payouts')} →
      </Link>
    </Card>
  )
}
